/**
 * Calendário operacional de Agendamento de Docas (#MT-B2FA396).
 * Mês / semana / dia — visual SIAL, sem biblioteca externa.
 * Datas exibidas em dd/MM/yyyy.
 */
import {
	statusLabel,
	formatDateBr,
	formatTime,
} from './input-dock-schedule-labels.js';

const WEEKDAYS = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];
const MONTHS = [
	'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
	'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro',
];

const STATUS_CLASS = {
	SCHEDULED: 'is-scheduled',
	IN_RECEIVING: 'is-receiving',
	COMPLETED: 'is-completed',
	CANCELLED: 'is-cancelled',
	NO_SHOW: 'is-noshow',
};

function esc(s)
{
	return String(s ?? '')
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;');
}

function startOfDay(d)
{
	return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function addDays(d, n)
{
	const x = new Date(d);
	x.setDate(x.getDate() + n);
	return x;
}

function ymd(d)
{
	return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function mondayOf(d)
{
	const x = startOfDay(d);
	const dow = x.getDay(); // 0=Dom
	const offset = dow === 0 ? -6 : 1 - dow;
	return addDays(x, offset);
}

function monthGridRange(cursor)
{
	const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
	const start = mondayOf(first);
	const last = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
	const endMonday = mondayOf(last);
	const end = addDays(endMonday, 7);
	return { start, end };
}

function weekRange(cursor)
{
	const start = mondayOf(cursor);
	return { start, end: addDays(start, 7) };
}

function dayRange(cursor)
{
	const start = startOfDay(cursor);
	return { start, end: addDays(start, 1) };
}

function sameDay(a, b)
{
	return a.getFullYear() === b.getFullYear()
		&& a.getMonth() === b.getMonth()
		&& a.getDate() === b.getDate();
}

function eventsOnDay(events, day)
{
	const key = ymd(day);
	return events.filter((ev) => ev._day === key);
}

function eventLabel(ev)
{
	const dock = ev.dock_name || (ev.dock_id != null ? `Doca ${ev.dock_id}` : '');
	const order = ev.input_order_id != null ? `Ped. ${ev.input_order_id}` : '';
	return [formatTime(ev.scheduled_start), dock, order].filter(Boolean).join(' · ');
}

function eventTitle(ev)
{
	return [
		`#${ev.id}`,
		statusLabel(ev.status),
		formatDateBr(ev.scheduled_start),
		`${formatTime(ev.scheduled_start)}–${formatTime(ev.scheduled_end)}`,
		ev.dock_name ? `Doca ${ev.dock_name}` : null,
		ev.input_order_id != null ? `Pedido ${ev.input_order_id}` : null,
		ev.has_occurrence ? 'Com ocorrência' : null,
	].filter(Boolean).join(' · ');
}

const CSS = `
#ids-cal-panel {
	background: #fff;
	padding: 0.4rem 0.75rem 1.2rem;
	height: 84vh;
	overflow: auto;
	box-sizing: border-box;
}
#ids-cal-panel * { box-sizing: border-box; }
#ids-cal-panel table { display: none !important; }
.ids-cal { font-family: inherit; color: #1f2937; }
.ids-cal-bar {
	display: flex; flex-wrap: wrap; align-items: center; gap: 0.45rem;
	margin: 0 0 0.65rem; padding: 0.5rem 0.65rem;
	background: #eef2ff; border: 1px solid #c7d2fe;
}
.ids-cal-title { font-weight: 700; color: #1e3a8a; font-size: 1.05rem; margin-right: 0.75rem; }
.ids-cal-bar button {
	border: 1px solid #c7d2fe; background: #fff; color: #1e3a8a;
	border-radius: 0.3rem; padding: 0.3rem 0.65rem; cursor: pointer; font-size: 0.85rem;
}
.ids-cal-bar button:hover { background: #e0e7ff; }
.ids-cal-bar button.is-on { background: #4c1d95; border-color: #4c1d95; color: #fff; }
.ids-cal-legend { display: flex; flex-wrap: wrap; gap: 0.7rem; font-size: 0.78rem; color: #374151; margin: 0 0 0.6rem; }
.ids-cal-dot { display: inline-block; width: 0.65rem; height: 0.65rem; border-radius: 2px; margin-right: 0.25rem; vertical-align: -1px; }
.ids-cal-weekgrid {
	display: grid;
	grid-template-columns: repeat(7, minmax(0, 1fr));
	gap: 1px;
	background: #cbd5e1;
	border: 1px solid #cbd5e1;
	width: 100%;
}
.ids-cal-hd {
	background: #1e3a8a;
	color: #fff;
	font-size: 0.75rem;
	font-weight: 600;
	text-align: center;
	padding: 0.4rem 0.2rem;
}
.ids-cal-cell {
	background: #fff;
	min-height: 6.4rem;
	padding: 0.3rem;
	min-width: 0;
	overflow: hidden;
}
.ids-cal-cell.is-week { min-height: 14rem; }
.ids-cal-cell.is-out { background: #f3f4f6; }
.ids-cal-cell.is-today { box-shadow: inset 0 0 0 2px #4c1d95; }
.ids-cal-num { font-size: 0.8rem; font-weight: 700; color: #1e3a8a; cursor: pointer; }
.ids-cal-cell.is-out .ids-cal-num { color: #9ca3af; }
.ids-chip {
	display: block; width: 100%; max-width: 100%;
	text-align: left; margin: 0.18rem 0 0;
	border: 0; border-left: 3px solid #2563eb; border-radius: 0.2rem;
	padding: 0.15rem 0.3rem; font-size: 0.7rem; cursor: pointer;
	background: #dbeafe; color: #1e3a8a;
	white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.ids-chip.is-scheduled { background: #dbeafe; color: #1e3a8a; border-left-color: #2563eb; }
.ids-chip.is-receiving { background: #ede9fe; color: #5b21b6; border-left-color: #7c3aed; }
.ids-chip.is-completed { background: #d1fae5; color: #065f46; border-left-color: #059669; }
.ids-chip.is-cancelled { background: #fee2e2; color: #991b1b; border-left-color: #dc2626; }
.ids-chip.is-noshow { background: #fee2e2; color: #7f1d1d; border-left-color: #7f1d1d; }
.ids-chip.is-occ { outline: 1px solid #f59e0b; }
.ids-more { display: block; border: 0; background: transparent; color: #4c1d95; font-size: 0.72rem; font-weight: 600; cursor: pointer; padding: 0.15rem 0; }
.ids-empty { color: #4b5563; padding: 0.75rem; font-size: 0.9rem; }
.ids-cal-daylist { display: flex; flex-direction: column; gap: 0.45rem; max-width: 52rem; }
.ids-cal-dayrow {
	display: flex; align-items: flex-start; gap: 0.75rem;
	padding: 0.55rem 0.7rem;
	background: #f8fafc;
	border: 1px solid #e5e7eb;
	border-radius: 0.4rem;
}
.ids-cal-hour {
	flex: 0 0 4.6rem;
	font-variant-numeric: tabular-nums;
	color: #6b7280;
	font-size: 0.82rem;
	line-height: 1.35;
}
.ids-cal-dayrow .ids-chip { width: auto; max-width: 100%; }
`;

function decorate(rows)
{
	return (Array.isArray(rows) ? rows : []).map((row) =>
	{
		const start = row.scheduled_start ? new Date(row.scheduled_start) : null;
		return {
			...row,
			_day: start && !Number.isNaN(start.getTime()) ? ymd(start) : '',
		};
	});
}

/**
 * @param {HTMLElement} target
 * @param {{ fetchRange: (fromIso: string, toIso: string) => Promise<object[]>, onOpen: (row: object) => void, onClose: () => void }} opts
 */
export function mountDockScheduleCalendar(target, { fetchRange, onOpen, onClose } = {})
{
	if (!target) return null;
	const state = {
		view: 'month',
		cursor: startOfDay(new Date()),
		events: [],
		loading: false,
	};

	let style = document.getElementById('ids-cal-css');
	if (!style)
	{
		style = document.createElement('style');
		style.id = 'ids-cal-css';
		document.head.appendChild(style);
	}
	style.textContent = CSS;

	target.innerHTML = [
		'<div class="ids-cal">',
		'<div class="ids-cal-bar">',
		'<button type="button" data-act="close">Voltar à lista</button>',
		'<button type="button" data-act="prev">Anterior</button>',
		'<button type="button" data-act="today">Hoje</button>',
		'<button type="button" data-act="next">Próximo</button>',
		'<span class="ids-cal-title"></span>',
		'<button type="button" data-view="month" class="is-on">Mês</button>',
		'<button type="button" data-view="week">Semana</button>',
		'<button type="button" data-view="day">Dia</button>',
		'</div>',
		'<div class="ids-cal-legend">',
		'<span><span class="ids-cal-dot" style="background:#2563eb"></span>Agendado</span>',
		'<span><span class="ids-cal-dot" style="background:#7c3aed"></span>Em recebimento</span>',
		'<span><span class="ids-cal-dot" style="background:#059669"></span>Concluído</span>',
		'<span><span class="ids-cal-dot" style="background:#dc2626"></span>Cancelado / Não compareceu</span>',
		'<span><span class="ids-cal-dot" style="background:#f59e0b"></span>Com ocorrência</span>',
		'</div>',
		'<div class="ids-cal-body"></div>',
		'</div>',
	].join('');

	const root = target.querySelector('.ids-cal');
	const titleEl = root.querySelector('.ids-cal-title');
	const bodyEl = root.querySelector('.ids-cal-body');
	if (!root || !titleEl || !bodyEl)
		return null;

	const visibleRange = () =>
	{
		if (state.view === 'week') return weekRange(state.cursor);
		if (state.view === 'day') return dayRange(state.cursor);
		return monthGridRange(state.cursor);
	};

	const setTitle = () =>
	{
		const c = state.cursor;
		if (state.view === 'day')
			titleEl.textContent = formatDateBr(c);
		else if (state.view === 'week')
		{
			const { start, end } = weekRange(c);
			titleEl.textContent = `${formatDateBr(start)} – ${formatDateBr(addDays(end, -1))}`;
		}
		else
			titleEl.textContent = `${MONTHS[c.getMonth()]} de ${c.getFullYear()}`;
		root.querySelectorAll('[data-view]').forEach((btn) =>
		{
			btn.classList.toggle('is-on', btn.dataset.view === state.view);
		});
	};

	const chipHtml = (ev) =>
	{
		const cls = STATUS_CLASS[ev.status] || 'is-scheduled';
		const occ = ev.has_occurrence ? ' is-occ' : '';
		return `<button type="button" class="ids-chip ${cls}${occ}" data-open="${esc(JSON.stringify([ev.company_id, ev.id]))}" title="${esc(eventTitle(ev))}">${esc(eventLabel(ev))}</button>`;
	};

	const renderMonth = () =>
	{
		const { start, end } = monthGridRange(state.cursor);
		const today = startOfDay(new Date());
		const days = [];
		for (let d = new Date(start); d < end; d = addDays(d, 1))
			days.push(new Date(d));
		const head = WEEKDAYS.map((w) => `<div class="ids-cal-hd">${w}</div>`).join('');
		const cells = days.map((d) =>
		{
			const inMonth = d.getMonth() === state.cursor.getMonth();
			const evs = eventsOnDay(state.events, d);
			const shown = evs.slice(0, 3);
			const extra = evs.length - shown.length;
			const cls = `ids-cal-cell${inMonth ? '' : ' is-out'}${sameDay(d, today) ? ' is-today' : ''}`;
			return `<div class="${cls}">
				<div class="ids-cal-num" data-day="${ymd(d)}">${d.getDate()}</div>
				${shown.map(chipHtml).join('')}
				${extra > 0 ? `<button type="button" class="ids-more" data-day="${ymd(d)}">+${extra} mais</button>` : ''}
			</div>`;
		}).join('');
		bodyEl.innerHTML = `<div class="ids-cal-weekgrid">${head}${cells}</div>`;
	};

	const renderWeek = () =>
	{
		const { start } = weekRange(state.cursor);
		const today = startOfDay(new Date());
		const head = WEEKDAYS.map((w) => `<div class="ids-cal-hd">${w}</div>`).join('');
		const cells = [];
		for (let i = 0; i < 7; i++)
		{
			const d = addDays(start, i);
			const evs = eventsOnDay(state.events, d);
			const cls = `ids-cal-cell is-week${sameDay(d, today) ? ' is-today' : ''}`;
			cells.push(`<div class="${cls}">
				<div class="ids-cal-num" data-day="${ymd(d)}">${formatDateBr(d)}</div>
				${evs.length ? evs.map(chipHtml).join('') : '<div class="ids-empty">Sem agenda</div>'}
			</div>`);
		}
		bodyEl.innerHTML = `<div class="ids-cal-weekgrid">${head}${cells.join('')}</div>`;
	};

	const renderDay = () =>
	{
		const evs = eventsOnDay(state.events, state.cursor)
			.slice()
			.sort((a, b) => String(a.scheduled_start).localeCompare(String(b.scheduled_start)));
		if (!evs.length)
		{
			bodyEl.innerHTML = `<div class="ids-cal-daylist"><div class="ids-empty">Nenhum agendamento em ${esc(formatDateBr(state.cursor))}.</div></div>`;
			return;
		}
		bodyEl.innerHTML = `<div class="ids-cal-daylist">${evs.map((ev) => `
			<div class="ids-cal-dayrow">
				<div class="ids-cal-hour">${esc(formatTime(ev.scheduled_start))}<br>${esc(formatTime(ev.scheduled_end))}</div>
				<div>${chipHtml(ev)}</div>
			</div>`).join('')}</div>`;
	};

	const paint = () =>
	{
		try
		{
			setTitle();
			if (state.view === 'week') renderWeek();
			else if (state.view === 'day') renderDay();
			else renderMonth();
		}
		catch (e)
		{
			bodyEl.textContent = `Falha ao montar o calendário: ${e?.message || e}`;
		}
	};

	const load = async () =>
	{
		const { start, end } = visibleRange();
		state.loading = true;
		paint();
		try
		{
			const rows = typeof fetchRange === 'function'
				? await fetchRange(ymd(start), ymd(end))
				: [];
			state.events = decorate(rows);
		}
		catch (e)
		{
			state.events = [];
			paint();
			const note = document.createElement('div');
			note.className = 'ids-empty';
			note.textContent = `Não foi possível carregar os agendamentos: ${e?.message || e}`;
			bodyEl.prepend(note);
			state.loading = false;
			return;
		}
		state.loading = false;
		paint();
	};

	const goDay = (iso) =>
	{
		const [y, m, d] = iso.split('-').map(Number);
		state.cursor = new Date(y, m - 1, d);
		state.view = 'day';
		void load();
	};

	root.addEventListener('click', (ev) =>
	{
		ev.stopPropagation();
		const close = ev.target.closest('[data-act="close"]');
		if (close) { onClose?.(); return; }
		const prev = ev.target.closest('[data-act="prev"]');
		const next = ev.target.closest('[data-act="next"]');
		const today = ev.target.closest('[data-act="today"]');
		if (prev || next || today)
		{
			if (today) state.cursor = startOfDay(new Date());
			else
			{
				const dir = prev ? -1 : 1;
				if (state.view === 'day') state.cursor = addDays(state.cursor, dir);
				else if (state.view === 'week') state.cursor = addDays(state.cursor, dir * 7);
				else state.cursor = new Date(state.cursor.getFullYear(), state.cursor.getMonth() + dir, 1);
			}
			void load();
			return;
		}
		const viewBtn = ev.target.closest('[data-view]');
		if (viewBtn)
		{
			state.view = viewBtn.dataset.view;
			void load();
			return;
		}
		const open = ev.target.closest('[data-open]');
		if (open)
		{
			try
			{
				const [company_id, id] = JSON.parse(open.dataset.open);
				const row = state.events.find((e) => String(e.company_id) === String(company_id) && Number(e.id) === Number(id));
				if (row) onOpen?.(row);
			}
			catch (_) { /* ignore */ }
			return;
		}
		const dayBtn = ev.target.closest('[data-day]');
		if (dayBtn) goDay(dayBtn.dataset.day);
	});

	void load();
	return {
		reload: load,
	};
}
